// 1:1 Intelligence Brief Mock Data Generator
import { COGNITIVE_LANES, teamMembers } from '../../mock';

// Generate random variation for demo purposes
const randomVariation = (base, variance) => {
  return Math.max(0, Math.min(100, base + (Math.random() - 0.5) * variance * 2));
};

// Generate sparkline data
const generateSparkline = (baseValue, trend = 'stable', points = 8) => {
  const data = [];
  let current = baseValue - (trend === 'up' ? 15 : trend === 'down' ? -15 : 0);
  
  for (let i = 0; i < points; i++) {
    const variation = (Math.random() - 0.5) * 10;
    const trendAdjust = trend === 'up' ? 3 : trend === 'down' ? -3 : 0;
    current = Math.max(0, Math.min(100, current + trendAdjust + variation));
    data.push(Math.round(current));
  }
  return data;
};

// Burnout risk drivers
const BURNOUT_DRIVERS = [
  { id: 'lane_misalignment', name: 'Lane Misalignment', description: 'Working outside primary cognitive lane' },
  { id: 'cognitive_complexity', name: 'Cognitive Complexity', description: 'High complexity tasks without adequate support' },
  { id: 'context_switching', name: 'Context Switching', description: 'Frequent shifts between different work types' },
  { id: 'task_overload', name: 'Task Overload', description: 'More tasks than sustainable capacity' },
  { id: 'deadline_pressure', name: 'Deadline Pressure', description: 'Multiple overlapping deadlines' },
  { id: 'insufficient_recovery', name: 'Insufficient Recovery', description: 'Extended periods without breaks' }
];

// Conversation topic categories
const TOPIC_CATEGORIES = {
  capacity: { icon: 'Briefcase', color: '#3B82F6', label: 'Capacity' },
  alignment: { icon: 'Target', color: '#8B5CF6', label: 'Alignment' },
  growth: { icon: 'TrendingUp', color: '#10B981', label: 'Growth' },
  wellbeing: { icon: 'Heart', color: '#F472B6', label: 'Wellbeing' },
  recognition: { icon: 'Star', color: '#F59E0B', label: 'Recognition' }
};

// Generate a full brief for a team member
export const generateBrief = (member, existingBriefs = []) => {
  // Handle case where member is just a string (participant name)
  if (typeof member === 'string') {
    member = {
      id: member.toLowerCase().replace(/\s/g, '-'),
      name: member,
      title: 'Team Member',
      role: 'Team Member',
      avatar: member.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
      current_load: Math.floor(Math.random() * 6) + 2,
      max_task_load: 8,
      lane_proficiency: {
        operations: 'L3',
        creative: 'L2'
      }
    };
  }
  
  // Ensure member has a name property
  if (!member || !member.name) {
    console.warn('Invalid member data passed to generateBrief:', member);
    member = {
      id: 'unknown-member',
      name: 'Unknown Member',
      title: 'Team Member',
      role: 'Team Member',
      avatar: '??',
      current_load: 4,
      max_task_load: 8,
      lane_proficiency: { operations: 'L2' }
    };
  }
  
  const now = new Date();
  const briefId = `brief-${member.id || member.name.replace(/\s/g, '-').toLowerCase()}-${Date.now()}`;
  
  // Get member's primary lane
  const lanes = Object.entries(member.lane_proficiency || {});
  const sortedLanes = lanes.sort((a, b) => {
    const levelOrder = { 'L4': 4, 'L3': 3, 'L2': 2, 'L1': 1 };
    return (levelOrder[b[1]] || 0) - (levelOrder[a[1]] || 0);
  });
  const primaryLane = sortedLanes[0];
  const primaryLaneData = COGNITIVE_LANES.find(l => l.id === primaryLane?.[0]);
  const primaryLevel = primaryLane?.[1] || 'L2';
  
  // Calculate load percentage
  const maxLoad = member.max_task_load || 8;
  const currentLoad = member.current_load || Math.floor(Math.random() * maxLoad);
  const loadPercentage = Math.round((currentLoad / maxLoad) * 100);
  
  // Generate cognitive status
  const laneAlignmentScore = Math.round(randomVariation(75, 20));
  const laneAlignmentTrend = laneAlignmentScore > 80 ? 'up' : laneAlignmentScore < 60 ? 'down' : 'stable';
  const levelAppropriateWork = Math.round(randomVariation(70, 25));
  const daysInSecondaryLane = Math.floor(Math.random() * 14);
  
  // Determine secondary lane being used
  const secondaryLanes = sortedLanes.slice(1);
  const activeSecondaryLane = secondaryLanes[0];
  const activeSecondaryLaneData = COGNITIVE_LANES.find(l => l.id === activeSecondaryLane?.[0]);
  
  // Generate burnout risk
  const baseBurnoutRisk = loadPercentage > 80 ? 65 : loadPercentage > 60 ? 45 : 30;
  const burnoutRiskScore = Math.round(randomVariation(baseBurnoutRisk, 20));
  const burnoutTrend = burnoutRiskScore > 60 ? 'up' : burnoutRiskScore < 40 ? 'down' : 'stable';
  const weeksAtLevel = Math.floor(Math.random() * 8) + 1;
  
  // Select burnout drivers
  const shuffledDrivers = [...BURNOUT_DRIVERS].sort(() => Math.random() - 0.5);
  const topDrivers = shuffledDrivers.slice(0, 2);
  
  // Generate performance metrics
  const onTimeRate = Math.round(randomVariation(82, 15));
  const onTimeRateLastMonth = Math.round(randomVariation(onTimeRate, 10));
  const activeTasks = currentLoad;
  const overdueTasks = loadPercentage > 80 ? Math.floor(Math.random() * 4) + 1 : Math.floor(Math.random() * 2);
  const avgCompletionTime = `${Math.floor(Math.random() * 3) + 2}.${Math.floor(Math.random() * 9)} days`;
  const avgCompletionTimeLastMonth = `${Math.floor(Math.random() * 3) + 2}.${Math.floor(Math.random() * 9)} days`;
  
  // Generate projects
  const projects = [
    { name: 'Platform Evolution', healthScore: Math.round(randomVariation(75, 20)), contribution: 'Key contributor' },
    { name: 'Q2 Initiative', healthScore: Math.round(randomVariation(80, 15)), contribution: 'Supporting' }
  ].filter(() => Math.random() > 0.3);
  
  // Generate notable wins
  const wins = [
    `Completed the ${projects[0]?.name || 'Q1 Sprint'} phase on time`,
    'Received positive stakeholder feedback on recent deliverables',
    'Successfully onboarded new team member'
  ].filter(() => Math.random() > 0.4).slice(0, 2);
  
  // Growth assessment
  const levelOrder = ['L1', 'L2', 'L3', 'L4'];
  const currentLevelIndex = levelOrder.indexOf(primaryLevel);
  const performingAboveLevel = Math.random() > 0.6 && currentLevelIndex < 3;
  const nextLevel = performingAboveLevel ? levelOrder[currentLevelIndex + 1] : null;
  
  // Generate conversation topics
  const topics = [];
  
  // Capacity topic
  if (loadPercentage > 75 || overdueTasks > 0) {
    topics.push({
      category: 'capacity',
      text: `Workload discussion — currently at ${loadPercentage}% capacity${overdueTasks > 0 ? ` with ${overdueTasks} overdue item${overdueTasks > 1 ? 's' : ''}` : ''}. Ask about blockers.`,
      priority: loadPercentage > 85 ? 'high' : 'medium'
    });
  }
  
  // Alignment topic
  if (laneAlignmentScore < 75 && daysInSecondaryLane > 7) {
    topics.push({
      category: 'alignment',
      text: `Lane alignment check-in — has been doing significant ${activeSecondaryLaneData?.name || 'secondary'} work despite ${primaryLaneData?.name || 'primary'} primary lane. Intentional or drifting?`,
      priority: 'medium'
    });
  }
  
  // Growth topic
  if (performingAboveLevel) {
    topics.push({
      category: 'growth',
      text: `Growth conversation — data suggests ${nextLevel} readiness. Is ${member.name.split(' ')[0]} ready for more complex ownership?`,
      priority: 'medium'
    });
  }
  
  // Wellbeing topic
  if (burnoutRiskScore > 50) {
    topics.push({
      category: 'wellbeing',
      text: `Burnout check — risk has increased. Ask open-ended questions about how they're feeling about work right now.`,
      priority: burnoutRiskScore > 70 ? 'high' : 'medium'
    });
  }
  
  // Recognition topic
  if (wins.length > 0) {
    topics.push({
      category: 'recognition',
      text: `Acknowledge — ${wins[0]}${loadPercentage > 70 ? ' while managing elevated load' : ''}. Name it.`,
      priority: 'low'
    });
  }
  
  // Add general development topic
  if (topics.length < 4) {
    topics.push({
      category: 'growth',
      text: `Development focus — discuss which ${secondaryLanes.length > 0 ? 'supporting lanes' : 'areas'} ${member.name.split(' ')[0]} wants to develop this quarter.`,
      priority: 'low'
    });
  }
  
  // Build the brief object
  const brief = {
    id: briefId,
    generatedAt: now.toISOString(),
    generatedBy: 'CognitiveOS Intelligence',
    member: {
      id: member.id,
      name: member.name,
      title: member.title,
      avatar: member.avatar,
      primaryLane: primaryLaneData,
      primaryLevel: primaryLevel,
      bio: member.bio
    },
    sections: {
      cognitiveStatus: {
        laneAlignmentScore,
        laneAlignmentTrend,
        levelAppropriateWork,
        primaryLane: primaryLaneData,
        activeSecondaryLane: activeSecondaryLaneData,
        daysInSecondaryLane,
        aiSummary: laneAlignmentScore < 70 
          ? `${member.name.split(' ')[0]} has been operating primarily in their secondary ${activeSecondaryLaneData?.name || 'Operations'} lane rather than their primary ${primaryLaneData?.name || 'Strategy'} lane for ${daysInSecondaryLane} days — consider discussing whether their current assignments reflect their strengths.`
          : `${member.name.split(' ')[0]} is well-aligned with their primary ${primaryLaneData?.name || 'Strategy'} lane and maintaining strong cognitive focus.`
      },
      workloadCapacity: {
        currentLoad,
        maxLoad,
        loadPercentage,
        activeTasks,
        overdueTasks,
        avgCompletionTime,
        avgCompletionTimeLastMonth,
        loadTrend: generateSparkline(loadPercentage, loadPercentage > 75 ? 'up' : 'stable'),
        alert: loadPercentage > 80 ? {
          type: 'warning',
          message: 'Currently at elevated load — consider whether new assignments are appropriate before this meeting.'
        } : null
      },
      burnoutRisk: {
        score: burnoutRiskScore,
        trend: burnoutTrend,
        weeksAtLevel,
        topDrivers,
        trendData: generateSparkline(burnoutRiskScore, burnoutTrend),
        aiInterpretation: burnoutRiskScore > 50 
          ? `${member.name.split(' ')[0]}'s burnout risk has increased ${Math.floor(Math.random() * 15) + 5} points over the past ${weeksAtLevel} weeks, driven primarily by ${topDrivers[0]?.name.toLowerCase()}${topDrivers[1] ? ` and ${topDrivers[1].name.toLowerCase()}` : ''}.`
          : `${member.name.split(' ')[0]}'s burnout risk is within healthy parameters with no significant risk factors detected.`,
        suggestedOpener: burnoutRiskScore > 50 
          ? `"I've noticed you've been juggling a lot of different types of work lately — how are you feeling about your current load and focus?"`
          : null
      },
      performanceContext: {
        onTimeRate,
        onTimeRateLastMonth,
        onTimeRateTrend: onTimeRate > onTimeRateLastMonth ? 'up' : onTimeRate < onTimeRateLastMonth ? 'down' : 'stable',
        projects,
        wins,
        phasesCompleted: Math.floor(Math.random() * 5) + 1,
        phasesMissed: Math.floor(Math.random() * 2)
      },
      growthDevelopment: {
        currentLevel: primaryLevel,
        performingAboveLevel,
        suggestedLevel: nextLevel,
        supportingLaneUsage: secondaryLanes.map(([laneId, level]) => ({
          lane: COGNITIVE_LANES.find(l => l.id === laneId),
          level,
          usagePercentage: Math.round(randomVariation(30, 20))
        })).slice(0, 3),
        aiInsight: performingAboveLevel 
          ? `Based on task complexity handled and decision patterns, ${member.name.split(' ')[0]} may be ready for ${nextLevel} responsibilities — consider discussing growth trajectory.`
          : `${member.name.split(' ')[0]} is operating at expected ${primaryLevel} level with opportunities for growth in supporting lanes.`
      },
      conversationTopics: topics.slice(0, 6)
    }
  };
  
  return brief;
};

// Get brief history for a team member
export const getBriefHistory = (memberId, count = 5) => {
  const history = [];
  const now = new Date();
  
  for (let i = 0; i < count; i++) {
    const pastDate = new Date(now);
    pastDate.setDate(pastDate.getDate() - (i * 14 + Math.floor(Math.random() * 7))); // ~2 weeks apart
    
    history.push({
      id: `history-${i}`,
      date: pastDate.toISOString(),
      cognitiveStatus: Math.round(randomVariation(75, 20)),
      burnoutRisk: Math.round(randomVariation(45, 25)),
      loadPercentage: Math.round(randomVariation(70, 20)),
      onTimeRate: Math.round(randomVariation(82, 15)),
      topicsCovered: Math.floor(Math.random() * 4) + 2
    });
  }
  
  return history.sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Export topic categories for use in components
export const TOPIC_CATEGORIES_CONFIG = TOPIC_CATEGORIES;

// Mock data for testing
export const BRIEF_MOCK_DATA = {
  generateBrief,
  getBriefHistory,
  TOPIC_CATEGORIES,
  BURNOUT_DRIVERS
};
